import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getReadingSessionsList } from '../../../../services/api/admin.service';

interface Session {
  id: number;
  user_id: number;
  session_start: string;
  session_end?: string;
  duration_ms?: number;
  total_words: number;
  correct_words: number;
  error_words: number;
  accuracy_percentage?: number;
  browser_name?: string;
  browser_version?: string;
  os_name?: string;
  device_type?: string;
  status: string;
  completion_reason?: string;
  Users?: {
    id: string;
    name: string;
    email: string;
  };
}

interface ReadingSessionsTableProps {
  schoolId?: string;
  daysAgo: number;
}

const ReadingSessionsTable: React.FC<ReadingSessionsTableProps> = ({ schoolId, daysAgo }) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [deviceFilter, setDeviceFilter] = useState<string>('');

  useEffect(() => {
    loadSessions();
  }, [schoolId, daysAgo, page, rowsPerPage, statusFilter, deviceFilter]);

  const loadSessions = async () => {
    setIsLoading(true);
    try {
      const data = await getReadingSessionsList(
        schoolId,
        daysAgo,
        page + 1,
        rowsPerPage,
        statusFilter || undefined,
        deviceFilter || undefined
      );
      setSessions(data.sessions);
      setTotal(data.total);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'abandoned':
        return 'warning';
      case 'crashed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const exportToPDF = async () => {
    try {
      // טעינת כל הנתונים (ללא pagination)
      const allData = await getReadingSessionsList(
        schoolId,
        daysAgo,
        1,
        10000, // מספר גדול מאוד כדי לקבל את כל הנתונים
        statusFilter || undefined,
        deviceFilter || undefined
      );

      const allSessions = allData.sessions || [];

      // יצירת PDF
      const doc = new jsPDF('landscape'); // אופקי כי יש הרבה עמודות
      
      // כותרת
      doc.setFontSize(16);
      doc.text(t('readingSessionsAnalytics'), 14, 15);
      
      // תאריך יצירה
      doc.setFontSize(10);
      doc.text(
        `${t('exportedOn')}: ${new Date().toLocaleString('he-IL')}`,
        14,
        22
      );

      // הכנת נתונים לטבלה
      const tableData = allSessions.map((session: Session) => [
        session.Users?.name || session.Users?.email || `User ${session.user_id}`,
        new Date(session.session_start).toLocaleString('he-IL'),
        formatDuration(session.duration_ms),
        `${session.correct_words}/${session.total_words}`,
        session.accuracy_percentage ? `${session.accuracy_percentage.toFixed(1)}%` : '-',
        session.device_type || '-',
        session.browser_name ? `${session.browser_name} ${session.browser_version || ''}` : '-',
        t(session.status),
      ]);

      // יצירת הטבלה
      autoTable(doc, {
        head: [[
          t('user'),
          t('startTime'),
          t('duration'),
          t('words'),
          t('accuracy'),
          t('device'),
          t('browser'),
          t('status'),
        ]],
        body: tableData,
        startY: 28,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [102, 126, 234], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 28 },
      });

      // שמירת הקובץ
      const fileName = `reading-sessions-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert(t('exportError') || 'שגיאה בייצוא לקובץ PDF');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('status')}</InputLabel>
          <Select
            value={statusFilter}
            label={t('status')}
            inputProps={{ 'aria-label': 'סינון לפי סטטוס' }}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">{t('all')}</MenuItem>
            <MenuItem value="completed">{t('completed')}</MenuItem>
            <MenuItem value="abandoned">{t('abandoned')}</MenuItem>
            <MenuItem value="crashed">{t('crashed')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('deviceType')}</InputLabel>
          <Select
            value={deviceFilter}
            label={t('deviceType')}
            inputProps={{ 'aria-label': 'סינון לפי סוג מכשיר' }}
            onChange={(e) => {
              setDeviceFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">{t('all')}</MenuItem>
            <MenuItem value="desktop">{t('desktop')}</MenuItem>
            <MenuItem value="mobile">{t('mobile')}</MenuItem>
            <MenuItem value="tablet">{t('tablet')}</MenuItem>
          </Select>
        </FormControl>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PictureAsPdfIcon />}
          onClick={exportToPDF}
          disabled={isLoading || total === 0}
        >
          {t('exportToPDF')}
        </Button>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('user')}</TableCell>
              <TableCell>{t('startTime')}</TableCell>
              <TableCell>{t('duration')}</TableCell>
              <TableCell>{t('words')}</TableCell>
              <TableCell>{t('accuracy')}</TableCell>
              <TableCell>{t('device')}</TableCell>
              <TableCell>{t('browser')}</TableCell>
              <TableCell>{t('status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : sessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {t('noSessionsFound')}
                </TableCell>
              </TableRow>
            ) : (
              sessions.map((session) => (
                <TableRow key={session.id}>
                  <TableCell>
                    {session.Users?.name || session.Users?.email || `User ${session.user_id}`}
                  </TableCell>
                  <TableCell>
                    {new Date(session.session_start).toLocaleString('he-IL')}
                  </TableCell>
                  <TableCell>{formatDuration(session.duration_ms)}</TableCell>
                  <TableCell>
                    {session.correct_words}/{session.total_words}
                  </TableCell>
                  <TableCell>
                    {session.accuracy_percentage ? `${session.accuracy_percentage.toFixed(1)}%` : '-'}
                  </TableCell>
                  <TableCell>{session.device_type || '-'}</TableCell>
                  <TableCell>
                    {session.browser_name ? `${session.browser_name} ${session.browser_version || ''}` : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t(session.status)}
                      color={getStatusColor(session.status) as any}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );
};

export default ReadingSessionsTable;

